import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Share2, Heart, ShoppingCart } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import { getImageUrl } from '../utils/imageUrl';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ProjectDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, addToCart } = useAuth();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [selectedServices, setSelectedServices] = useState(['arquitetonico']);
  const [projectAddress, setProjectAddress] = useState('');
  const [showAddressField, setShowAddressField] = useState(false);
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [addingToCart, setAddingToCart] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchProject();
  }, [id]);

  const fetchProject = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/projects/${id}`);
      setProject(response.data);
    } catch (error) {
      console.error('Erro ao carregar projeto:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-600">Carregando projeto...</p>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-600">Projeto não encontrado</p>
      </div>
    );
  }

  const additionalServices = project ? [
    project.showInterior !== false && { id: 'interiores', name: 'Interiores', price: project.interiorPrice || 1618.50 },
    project.showRRT !== false && { id: 'rrt', name: 'RRT + Assessoria Técnica (Recomendado para Cliente Final)', price: project.rrtPrice || 1300.00 }
  ].filter(Boolean) : [];

  const projectImages = project.images && project.images.length > 0 
    ? project.images.map(img => getImageUrl(img))
    : [project.image || 'https://via.placeholder.com/800x600'];

  const toggleService = (serviceId) => {
    if (serviceId === 'arquitetonico') return;
    
    if (serviceId === 'rrt') {
      const isAdding = !selectedServices.includes(serviceId);
      setShowAddressField(isAdding);
      if (!isAdding) {
        setProjectAddress('');
      }
    }
    
    setSelectedServices(prev => 
      prev.includes(serviceId)
        ? prev.filter(id => id !== serviceId)
        : [...prev, serviceId]
    );
  };

  const calculateTotal = () => {
    // Calculate base price with discount
    const discountMultiplier = project.discount > 0 ? (100 - project.discount) / 100 : 1;
    let total = project.price * discountMultiplier;
    
    // Add additional services
    selectedServices.forEach(serviceId => {
      const service = additionalServices.find(s => s.id === serviceId);
      if (service) total += service.price;
    });
    
    return total;
  };

  const getOriginalTotal = () => {
    let total = project.price;
    selectedServices.forEach(serviceId => {
      const service = additionalServices.find(s => s.id === serviceId);
      if (service) total += service.price;
    });
    return total;
  };

  const formatPrice = (price) => {
    return price.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Back Button */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Button
          variant="ghost"
          onClick={() => navigate('/')}
          className="flex items-center space-x-2 hover:text-[#00A86B]"
        >
          <ArrowLeft size={20} />
          <span>Voltar aos projetos</span>
        </Button>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* Left Column - Images and Details */}
          <div className="lg:col-span-2 space-y-8">
            
            {/* Image Gallery */}
            <div className="relative">
              {project.showDiscount && project.discount > 0 && (
                <div className="absolute top-4 right-4 bg-black text-white px-6 py-3 rounded-full font-bold text-xl z-10 shadow-xl">
                  {project.discount}% OFF
                </div>
              )}
              <img
                src={projectImages[currentImageIndex]}
                alt={project.name}
                className="w-full h-96 object-cover rounded-xl shadow-lg"
              />
              <div className="flex gap-2 mt-4 overflow-x-auto pb-2">
                {projectImages.map((img, idx) => (
                  <img
                    key={idx}
                    src={img}
                    alt={`${project.name} ${idx + 1}`}
                    className={`w-24 h-24 object-cover rounded-lg cursor-pointer border-2 transition-all hover:border-[#00A86B] ${
                      currentImageIndex === idx ? 'border-[#00A86B] shadow-lg' : 'border-gray-200'
                    }`}
                    onClick={() => setCurrentImageIndex(idx)}
                  />
                ))}
              </div>
              {projectImages.length > 1 && (
                <p className="text-sm text-gray-600 text-center mt-2">
                  {currentImageIndex + 1} / {projectImages.length} imagens
                </p>
              )}
            </div>

            {/* Project Title and Actions */}
            <div>
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h1 className="text-4xl font-bold text-gray-800 mb-2">{project.name}</h1>
                  <div className="flex space-x-2">
                    {project.tags.map((tag, index) => (
                      <Badge 
                        key={index} 
                        variant="secondary"
                        className="bg-gray-800 text-white rounded-full px-3 py-1"
                      >
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
                <div className="flex space-x-2">
                  <Button variant="ghost" size="icon" className="hover:text-[#00A86B]">
                    <Share2 size={20} />
                  </Button>
                  <Button variant="ghost" size="icon" className="hover:text-[#00A86B]">
                    <Heart size={20} />
                  </Button>
                </div>
              </div>
            </div>

            {/* Características do Projeto */}
            <Card className="p-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Características do projeto</h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <div className="text-center">
                  <p className="text-sm text-gray-600 mb-2">Terreno</p>
                  <p className="text-lg font-semibold text-gray-800">{project.terrain}</p>
                </div>
                <div className="text-center">
                  <p className="text-sm text-gray-600 mb-2">Área</p>
                  <p className="text-lg font-semibold text-gray-800">{project.area}m²</p>
                </div>
                <div className="text-center">
                  <p className="text-sm text-gray-600 mb-2">Quartos</p>
                  <p className="text-lg font-semibold text-gray-800">{project.bedrooms}</p>
                </div>
                <div className="text-center">
                  <p className="text-sm text-gray-600 mb-2">Banheiros</p>
                  <p className="text-lg font-semibold text-gray-800">{project.bathrooms}</p>
                </div>
              </div>
            </Card>

            {/* Descrição */}
            <Card className="p-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-4">Descrição do projeto</h2>
              <p className="text-gray-700 leading-relaxed">{project.description}</p>
            </Card>

            {/* Ficha Técnica */}
            <Card className="p-6">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Ficha técnica</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Column 1 */}
                <div className="space-y-4">
                  <div className="flex justify-between py-2 border-b border-gray-200">
                    <span className="text-gray-600 font-medium">Terreno:</span>
                    <span className="font-semibold text-gray-800">{project.terrain}</span>
                  </div>
                  {project.terrainType && (
                    <div className="flex justify-between py-2 border-b border-gray-200">
                      <span className="text-gray-600 font-medium">Tipo de Terreno:</span>
                      <span className="font-semibold text-gray-800">{project.terrainType}</span>
                    </div>
                  )}
                  <div className="flex justify-between py-2 border-b border-gray-200">
                    <span className="text-gray-600 font-medium">Área construída:</span>
                    <span className="font-semibold text-gray-800">{project.area}m²</span>
                  </div>
                  {project.houseWidth && (
                    <div className="flex justify-between py-2 border-b border-gray-200">
                      <span className="text-gray-600 font-medium">Largura da Casa:</span>
                      <span className="font-semibold text-gray-800">{project.houseWidth}m</span>
                    </div>
                  )}
                  {project.houseLength && (
                    <div className="flex justify-between py-2 border-b border-gray-200">
                      <span className="text-gray-600 font-medium">Comprimento da Casa:</span>
                      <span className="font-semibold text-gray-800">{project.houseLength}m</span>
                    </div>
                  )}
                </div>

                {/* Column 2 */}
                <div className="space-y-4">
                  {project.facadeType && (
                    <div className="flex justify-between py-2 border-b border-gray-200">
                      <span className="text-gray-600 font-medium">Tipo de Fachada:</span>
                      <span className="font-semibold text-gray-800">{project.facadeType}</span>
                    </div>
                  )}
                  {project.garageSpaces > 0 && (
                    <div className="flex justify-between py-2 border-b border-gray-200">
                      <span className="text-gray-600 font-medium">Garagem:</span>
                      <span className="font-semibold text-gray-800">{project.garageSpaces} {project.garageSpaces === 1 ? 'vaga' : 'vagas'}</span>
                    </div>
                  )}
                </div>
              </div>
            </Card>
          </div>

          {/* Right Column - Purchase Section */}
          <div className="lg:col-span-1">
            <div className="sticky top-24">
              <Card className="p-6 shadow-xl border-2 border-gray-200">
                <h3 className="text-lg font-bold text-gray-800 mb-4">Condições especiais</h3>
                
                {/* Discount Banner */}
                {project.showDiscount && project.discount > 0 && (
                  <div className="bg-gradient-to-r from-red-500 to-red-600 text-white p-4 rounded-lg mb-4 shadow-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-semibold uppercase tracking-wide">Desconto Especial</p>
                        <p className="text-3xl font-bold">{project.discount}% OFF</p>
                      </div>
                      <div className="text-5xl font-bold opacity-20">%</div>
                    </div>
                    <p className="text-xs mt-2 opacity-90">Economia garantida neste projeto!</p>
                  </div>
                )}
                
                <div className="bg-gradient-to-r from-[#00A86B] to-[#008c5a] text-white p-4 rounded-lg mb-6">
                  <p className="text-sm mb-2">Parcele em até</p>
                  <p className="text-2xl font-bold">10x de R$ {formatPrice(calculateTotal() / 10)}</p>
                  <p className="text-sm">sem juros no cartão</p>
                </div>

                {/* Base Project */}
                <div className="mb-6">
                  <div className="flex items-center space-x-3 mb-4 p-3 bg-green-50 rounded-lg border border-green-200">
                    <Checkbox checked={true} disabled className="border-[#00A86B]" />
                    <div className="flex-1">
                      <p className="font-semibold text-gray-800">Arquitetônico</p>
                    </div>
                    <p className="font-bold text-[#00A86B]">R$ {formatPrice(project.price)}</p>
                  </div>

                  <p className="text-sm font-semibold text-gray-700 mb-3">Projetos complementares</p>
                  
                  {additionalServices.map(service => (
                    <div 
                      key={service.id}
                      className="flex items-center space-x-3 mb-3 p-3 hover:bg-gray-50 rounded-lg cursor-pointer transition-all"
                      onClick={() => toggleService(service.id)}
                    >
                      <Checkbox 
                        checked={selectedServices.includes(service.id)}
                        className="border-gray-400"
                      />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-700">{service.name}</p>
                      </div>
                      <p className="text-sm font-semibold text-gray-600">R$ {formatPrice(service.price)}</p>
                    </div>
                  ))}
                </div>

                {/* RRT Information */}
                {showAddressField && (
                  <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div className="space-y-3">
                      <p className="font-bold text-gray-800">RRT com validade nacional</p>
                      <p className="text-sm text-gray-700 leading-relaxed">
                        Após a confirmação do pagamento, nossa equipe entrará em contato em até <span className="font-bold">24 horas úteis</span> para validar os dados necessários à emissão do RRT (Registro de Responsabilidade Técnica) e dar continuidade à assessoria.
                      </p>
                    </div>
                  </div>
                )}

                {/* Total */}
                <div className="border-t pt-4 mb-6">
                  {project.discount > 0 && (
                    <div className="mb-3">
                      <div className="flex justify-between items-center text-sm">
                        <span className="text-gray-600">Valor original:</span>
                        <span className="text-gray-500 line-through">R$ {formatPrice(getOriginalTotal())}</span>
                      </div>
                      <div className="flex justify-between items-center text-sm text-[#00A86B] font-semibold">
                        <span>Desconto ({project.discount}%):</span>
                        <span>- R$ {formatPrice(getOriginalTotal() - calculateTotal())}</span>
                      </div>
                    </div>
                  )}
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-lg font-semibold text-gray-800">Total</span>
                    <span className="text-3xl font-bold text-[#00A86B]">R$ {formatPrice(calculateTotal())}</span>
                  </div>
                  <p className="text-sm text-gray-600 text-center">em até 10x de R$ {formatPrice(calculateTotal() / 10)} sem juros</p>
                </div>

                {/* Important Notice */}
                <div className="mb-6 p-4 bg-yellow-50 border border-yellow-300 rounded-lg">
                  <p className="text-sm text-gray-700 font-medium">
                    <span className="font-bold text-yellow-800">Atenção:</span> o projeto escolhido deve ser compatível com as dimensões do seu terreno.
                  </p>
                </div>

                {/* Action Buttons */}
                <div className="space-y-3">
                  <Button 
                    className="w-full bg-white border-2 border-[#00A86B] text-[#00A86B] hover:bg-[#00A86B] hover:text-white py-6 text-lg font-bold shadow-lg hover:shadow-xl transition-all"
                    onClick={async () => {
                      if (selectedServices.length === 0) {
                        alert('Selecione pelo menos um serviço');
                        return;
                      }
                      
                      setAddingToCart(true);
                      try {
                        await addToCart(project.id, selectedServices, project);
                        alert('Projeto adicionado ao carrinho!');
                      } catch (error) {
                        if (error.message === 'Projeto já está no carrinho') {
                          alert('Este projeto já está no seu carrinho');
                        } else if (error.response?.status === 400) {
                          alert('Este projeto já está no seu carrinho');
                        } else {
                          alert('Erro ao adicionar ao carrinho');
                        }
                      } finally {
                        setAddingToCart(false);
                      }
                    }}
                    disabled={addingToCart}
                  >
                    <ShoppingCart className="mr-2" size={20} />
                    {addingToCart ? 'ADICIONANDO...' : 'ADICIONAR AO CARRINHO'}
                  </Button>
                  
                  <Button 
                    className="w-full bg-[#00A86B] hover:bg-[#008c5a] text-white py-6 text-lg font-bold shadow-lg hover:shadow-xl transition-all"
                    onClick={() => {
                      navigate('/checkout', {
                        state: {
                          project,
                          selectedServices,
                          total: calculateTotal()
                        }
                      });
                    }}
                  >
                    COMPRAR PROJETO
                  </Button>
                  
                  {/* Security Badge */}
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 mt-2">
                    <div className="flex items-center justify-center gap-2 text-sm text-gray-700">
                      <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span className="font-semibold">Compra 100% segura</span>
                      <span className="text-gray-500">•</span>
                      <span className="font-medium text-blue-600">Mercado Pago</span>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default ProjectDetails;